import { useState, useContext } from "react";
import { collection, addDoc, updateDoc, deleteDoc, doc } from "firebase/firestore";
import ReactQuill from "react-quill";
import "react-quill/dist/quill.snow.css";
import { EncryptionContext } from "./EncryptionContext.jsx";
import { encryptText, getEncryptionSecret } from "../utils";

const promptEditorModules = {
  toolbar: [
    [{ header: [1, 2, false] }],
    ["bold", "italic", "underline"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["link"],
    ["clean"],
  ],
};

const promptEditorFormats = [
  "header",
  "bold",
  "italic",
  "underline",
  "list",
  "bullet",
  "link",
];

function isBlankHtml(value) {
  if (!value) return true;
  const withoutTags = value.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim();
  return withoutTags.length === 0;
}

function containsHtml(value = "") {
  return /<[^>]+>/.test(value);
}

function escapeHtml(value = "") {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

function getPromptDisplayHtml(value = "") {
  return containsHtml(value) ? value : `<p>${escapeHtml(value)}</p>`;
}

function PromptsTab({ user, db, selectedCycle, prompts, setPrompts, setDecryptionError }) {
  const { passphrase } = useContext(EncryptionContext);
  const [newPrompt, setNewPrompt] = useState("");
  const [editPromptId, setEditPromptId] = useState(null);
  const [editPromptText, setEditPromptText] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [saveConfirmation, setSaveConfirmation] = useState("");

  const addPrompt = async () => {
    if (!user || !selectedCycle || !newPrompt.trim()) return;
    setLoading(true);
    try {
      const encryptionSecret = getEncryptionSecret(user, passphrase);
      const encryptedText = await encryptText(newPrompt, user, encryptionSecret);
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      const docRef = await addDoc(collection(db, `${cyclePath}/prompts`), {
        text: encryptedText,
      });
      setPrompts([...prompts, { id: docRef.id, text: newPrompt }]);
      setNewPrompt("");
      setSaveConfirmation("Prompt added successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error adding prompt. Please try again.");
      setDecryptionError("Error adding prompt. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const startEditPrompt = (prompt) => {
    setEditPromptId(prompt.id);
    setEditPromptText(prompt.text);
  };

  const saveEditPrompt = async () => {
    if (!user || !selectedCycle || isBlankHtml(editPromptText) || !editPromptId) return;
    setLoading(true);
    try {
      const encryptionSecret = getEncryptionSecret(user, passphrase);
      const encryptedText = await encryptText(editPromptText, user, encryptionSecret);
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      await updateDoc(doc(db, `${cyclePath}/prompts`, editPromptId), {
        text: encryptedText,
      });
      setPrompts(
        prompts.map((p) =>
          p.id === editPromptId ? { ...p, text: editPromptText } : p
        )
      );
      setEditPromptId(null);
      setEditPromptText("");
      setSaveConfirmation("Prompt updated successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error updating prompt. Please try again.");
      setDecryptionError("Error updating prompt. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const deletePrompt = async (promptId) => {
    if (!user || !selectedCycle) return;
    setLoading(true);
    try {
      const cyclePath = `users/${user.uid}/cycles/${selectedCycle.id}`;
      await deleteDoc(doc(db, `${cyclePath}/prompts`, promptId));
      setPrompts(prompts.filter((p) => p.id !== promptId));
      setSaveConfirmation("Prompt deleted successfully!");
      setTimeout(() => setSaveConfirmation(""), 3000);
      setError("");
      setDecryptionError("");
    } catch (error) {
      setError("Error deleting prompt. Please try again.");
      setDecryptionError("Error deleting prompt. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="card animate-fade-in w-full mt-0">
      <h2 className="text-2xl font-semibold text-text-light dark:text-text-dark mb-4">Manage Prompts</h2>
      {error && (
        <div className="mb-4 p-2 bg-red-100 dark:bg-red-900 text-error rounded">
          {error}
        </div>
      )}
      {saveConfirmation && (
        <div className="mb-4 p-2 bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300 rounded">
          {saveConfirmation}
        </div>
      )}
      {loading && (
        <div className="flex justify-center items-center my-4">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary" aria-label="Loading" />
        </div>
      )}
      {!loading && (
        <>
          <div className="mb-6">
            <label className="block text-text-light dark:text-text-dark font-medium mb-1">
              Add New Prompt
            </label>
            <textarea
              value={newPrompt}
              onChange={(e) => setNewPrompt(e.target.value)}
              className="input w-full h-24"
              placeholder="Enter a new prompt..."
              aria-label="New prompt input"
            />
            <button
              onClick={addPrompt}
              className="btn-primary mt-2"
              disabled={loading || !newPrompt.trim()}
              aria-label="Add new prompt"
            >
              Add Prompt
            </button>
          </div>
          <h3 className="text-lg font-medium text-text-light dark:text-text-dark mb-2">Existing Prompts</h3>
          {prompts.length > 0 ? (
            <ul className="space-y-4">
              {prompts.map((prompt) => (
                <li key={prompt.id} className="flex flex-col gap-3 rounded-md border border-gray-200 p-3 dark:border-gray-700">
                  {editPromptId === prompt.id ? (
                    <>
                      <ReactQuill
                        theme="snow"
                        value={editPromptText}
                        onChange={setEditPromptText}
                        modules={promptEditorModules}
                        formats={promptEditorFormats}
                        className="prompt-rich-editor bg-background-light dark:bg-background-dark text-text-light dark:text-text-dark"
                        placeholder="Edit prompt..."
                        aria-label="Edit prompt text"
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={saveEditPrompt}
                          className="btn-primary text-sm"
                          disabled={loading || isBlankHtml(editPromptText)}
                          aria-label="Save edited prompt"
                        >
                          Save
                        </button>
                        <button
                          onClick={() => setEditPromptId(null)}
                          className="btn-secondary text-sm"
                          disabled={loading}
                          aria-label="Cancel editing prompt"
                        >
                          Cancel
                        </button>
                      </div>
                    </>
                  ) : (
                    <>
                      <div
                        className="prompt-content flex-1 text-text-light dark:text-text-dark"
                        dangerouslySetInnerHTML={{ __html: getPromptDisplayHtml(prompt.text) }}
                      />
                      <div className="flex gap-2">
                        <button
                          onClick={() => startEditPrompt(prompt)}
                          className="btn-secondary text-sm"
                          disabled={loading}
                          aria-label={`Edit prompt ${prompt.text.replace(/<[^>]+>/g, "")}`}
                        >
                          Edit
                        </button>
                        <button
                          onClick={() => deletePrompt(prompt.id)}
                          className="btn-secondary text-error text-sm"
                          disabled={loading}
                          aria-label={`Delete prompt ${prompt.text.replace(/<[^>]+>/g, "")}`}
                        >
                          Delete
                        </button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-neutral">No prompts available. Add a new prompt above.</p>
          )}
        </>
      )}
    </div>
  );
}

export default PromptsTab;
